import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";

import { SOUND_NAME } from "../../../sounds";
import anime from "animejs/lib/anime.es.js";
import PotCollectionMaxWinDialogController from "./PotCollectionMaxWinDialogController";
import { safeInit, attemptFullscreenToggle } from "../../../utilities";
import { selectShowPotCollectionMaxWin } from "../../../State/Slices/animationsSlice";
import {
  selectPotCollectionCurrentRound,
  selectBet,
} from "../../../State/selectors";
import { soundManager } from "../../../SoundManager";



/**
 * PotCollectionMaxWinDialog
 *
 * Max Win (10,000x) popup for the Pot Collection bonus game — GDD §18.
 * Shown when win collection counter reaches the 10,000x cap.
 *
 * Identical structure to PotCollectionOutroDialog but:
 *   - Uses max_win skin instead of total_win
 *   - NO auto-close (GDD §16.4: explicit user click only)
 *   - Same transition as total_win on dismiss (back to base game)
 */
const PotCollectionMaxWinDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showMaxWin = useSelector(selectShowPotCollectionMaxWin);
  const potCollectionCurrentRound = useSelector(
    selectPotCollectionCurrentRound,
  );
  const bet = useSelector(selectBet);

  const maxWinControllerRef = useRef(null);
  const maxWinContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleUserDismissRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;

  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: POPUP_OVERLAY_CONFIG.ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: POPUP_OVERLAY_CONFIG.ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (maxWinContainerRef.current && !maxWinControllerRef.current) {
      safeInit(() => {
        maxWinControllerRef.current = new PotCollectionMaxWinDialogController(
          maxWinContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );
        // "TAP TO CONTINUE" text routes through the same dismiss flow (fullscreen
        // + sound + close) as the full-screen overlay. Proxy via ref so it always
        // calls the latest handleUserDismiss, not a stale mount-time closure.
        maxWinControllerRef.current.setButtonClickHandler((...args) => {
          if (handleUserDismissRef.current) handleUserDismissRef.current(...args);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (maxWinControllerRef.current) {
      maxWinControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Trigger the sequence when showMaxWin becomes true
  useEffect(() => {
    if (maxWinControllerRef.current && showMaxWin) {
      const totalWin = potCollectionCurrentRound?.win || 0;
      maxWinControllerRef.current.setTotalWinValue(totalWin, bet);
      maxWinControllerRef.current.startSequence();
    }
  }, [showMaxWin, potCollectionCurrentRound, bet]);

  // Handle user click: fullscreen (if needed) + dismiss
  const handleUserDismiss = useCallback(
    async (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      await attemptFullscreenToggle(dispatch, device);

      if (maxWinControllerRef.current && showMaxWin) {
        const accepted = maxWinControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showMaxWin, dispatch, device],
  );

  // Keep ref current so the controller's proxy always calls the latest version
  handleUserDismissRef.current = handleUserDismiss;

  // Spacebar handling (GDD §16.4: explicit click — spacebar counts as explicit)
  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showMaxWin || !maxWinControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        await handleUserDismiss(event);
      } catch {
        if (
          maxWinControllerRef.current &&
          maxWinControllerRef.current.finishAnimation
        ) {
          maxWinControllerRef.current.finishAnimation();
        }
      }
    };

    if (showMaxWin) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showMaxWin, handleUserDismiss]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (maxWinControllerRef.current) {
        maxWinControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(POPUP_OVERLAY_CONFIG.COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="pot_collection_max_win"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={11}
      eventMode={showMaxWin ? "auto" : "none"}
      ref={maxWinContainerRef}
    >
      {/* Background overlay */}
      <Graphics
        name="pot_collection_max_win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showMaxWin ? "static" : "none"}
        pointerdown={handleUserDismiss}
        cursor="default"
      />

      {/* Spine animation, counter, and button managed by PotCollectionMaxWinDialogController */}
    </Container>
  );
};

export default memo(PotCollectionMaxWinDialog);
